import { useQuery } from '@tanstack/vue-query';
import pluralize from 'pluralize';
import type { MaybeRefOrGetter, Ref } from 'vue';
import { computed, toValue } from 'vue';

import { sdk } from '@/sdk';
import { useProfile, useTaskOverviewStore } from '@/store';

export const TASK_DASHBOARD_KEYS = {
  dashboard: (assigneeIds: string[] | undefined) => ['taskDashboard', assigneeIds] as const,
};

export function useTaskAssigneeIds() {
  const profileStore = useProfile();
  const taskOverviewStore = useTaskOverviewStore();

  const isAdmin = computed(() => profileStore.profile?.is_org_admin);

  const assigneeIds = computed(() => {
    if (!isAdmin.value && profileStore.profile?.id) {
      return [profileStore.profile.id];
    }
    if (taskOverviewStore.selectedAssignee?.id) {
      return [taskOverviewStore.selectedAssignee.id];
    }
    return undefined;
  });

  return assigneeIds;
}

export function useTaskDashboard() {
  const assigneeIds = useTaskAssigneeIds();

  const { data, isLoading } = useQuery({
    placeholderData: (previousData) => previousData,
    queryFn: () => sdk.analytics.getDashboard({ assigneeIds: assigneeIds.value }),
    queryKey: computed(() => TASK_DASHBOARD_KEYS.dashboard(assigneeIds.value)),
    staleTime: 1000 * 60 * 5,
  });

  return { data, isLoading, assigneeIds };
}

export function useAgentBullets(data: Ref<any>, baseQuery: MaybeRefOrGetter<Record<string, any>>) {
  return computed(() => {
    const counts = data.value?.agentTaskLog as
      | Array<{ taskType: string; count: number }>
      | undefined;

    if (!counts) return [];

    const items: Array<{ taskType: string; label: string; route: object }> = [];

    for (const { taskType, count } of counts) {
      if (!count) continue;
      if (taskType === 'disable_contact') {
        items.push({
          taskType,
          label: `${pluralize('contact', count, true)} disabled`,
          route: {
            name: 'tasks.agent-log',
            query: { ...toValue(baseQuery), task_type: 'disable_contact' },
          },
        });
      } else if (taskType === 'payment_promise') {
        items.push({
          taskType,
          label: `Recorded payment promise dates for ${pluralize('invoice', count, true)}`,
          route: {
            name: 'tasks.agent-log',
            query: { ...toValue(baseQuery), task_type: 'payment_promise' },
          },
        });
      } else if (['auto_response_email', 'auto_response_sms', 'call_analysis'].includes(taskType)) {
        items.push({
          taskType,
          label: `Sent ${pluralize('automated response', count, true)}`,
          route: {
            name: 'tasks.agent-log',
            query: { ...toValue(baseQuery), task_type: 'email_response' },
          },
        });
      }
    }

    return items;
  });
}
